import { useEffect } from 'react';
import { useLocale } from '../../content';
import { useLocation } from 'react-router-dom';
import { pathForLocale } from './LocaleToggle';
import { useShopConfig } from '../../hooks/useShopConfig';

/**
 * Document head management without a dependency.
 *
 * Also emits LocalBusiness JSON-LD from shop.config.json — hours, address
 * and rating are structured data Google reads, so they must come from the
 * same source as the visible copy or the two will disagree.
 */
export default function SEO({ title, description }: { title: string; description: string }) {
  const locale = useLocale();
  const { pathname } = useLocation();
  const { shop, hours, reviews } = useShopConfig();

  useEffect(() => {
    document.title = title;
    document.documentElement.lang = locale;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.name = name;
        document.head.appendChild(el);
      }
      el.content = content;
    };
    setMeta('description', description);

    // hreflang: both language versions of THIS page, plus x-default.
    // Absolute URLs — Google ignores relative alternates. x-default points at
    // English, which is what an unmatched visitor gets today; it states the
    // fallback, it does not redirect anyone.
    const origin = window.location.origin;
    const alternates: Array<[string, string]> = [
      ['en', origin + pathForLocale(pathname, 'en')],
      ['es', origin + pathForLocale(pathname, 'es')],
      ['x-default', origin + pathForLocale(pathname, 'en')],
    ];
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    for (const [lang, href] of alternates) {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = lang;
      link.href = href;
      document.head.appendChild(link);
    }

    const ld = {
      '@context': 'https://schema.org',
      '@type': 'HairSalon',
      name: shop.name,
      telephone: shop.phone_e164,
      address: {
        '@type': 'PostalAddress',
        streetAddress: shop.address_line_1,
        addressLocality: shop.city,
        addressRegion: shop.state,
        postalCode: shop.zip,
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: reviews.combined_rating,
        reviewCount: reviews.combined_total,
      },
      openingHoursSpecification: hours
        .filter((h) => h.open && h.close)
        .map((h) => ({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: h.day,
          opens: h.open,
          closes: h.close,
        })),
    };

    let script = document.getElementById('kd-jsonld');
    if (!script) {
      script = document.createElement('script');
      script.id = 'kd-jsonld';
      (script as HTMLScriptElement).type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);
  }, [title, description, locale, pathname, shop, hours, reviews]);

  return null;
}
